import {
  Box,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Button,
  DeleteConfirmationModal,
  DeleteIcon,
  Input,
  PlusIcon,
} from "components";
import {
  useDeleteSsoConfig,
  useGetCustomerSsoDetails,
  useGlobalStore,
  useUpdateCustomerDetails,
} from "controller";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
// @ts-ignore
import dataDashboardImg from "assets/images/chartImgs/dataDashboardImg.svg";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useGetDoesHaveCustomerFullAccess } from "pages/modules/customer/context";

interface domainNameData {
  domain_name: Array<string>;
  country: string | number;
}

const domainSchema = yup.object({
  domain: yup
    .string()
    .matches(
      /^(?:(?:https?|ftp):\/\/)?[A-Za-z0-9-]{1,63}(\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,}$/,
      { message: "Please enter valid domain" },
    ),
});

const DomainComponent = ({
  domainNameData,
  setDrawweOpen,
  ssoDetails,
}: {
  domainNameData: domainNameData;
  setDrawweOpen: (data: any) => void;
  ssoDetails: any;
}) => {
  const theme = useTheme();
  const haveFullAccess = useGetDoesHaveCustomerFullAccess();
  const {
    mutateAsync: updateCustomerDetails,
    isLoading: isUpdateCustomerDetailsLoading,
  } = useUpdateCustomerDetails();
  const [domains, setDomains] = useState<any>([]);
  const [showTextField, setShowTextField] = useState(false);
  const [newDomain, setNewDomain] = useState("");

  const { id: customerId }: any = useParams();
  const defaultCustomerId = useGlobalStore((state) => state.defaultCustomer);
  const isDefaultCustomer = defaultCustomerId === customerId;

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletModalList, setDeleteModallist] = useState<any>([]);
  const [ssoDomainIdToBeDeleted, setSsoDomainIdToBeDeleted] =
    useState<string>();
  const [isSsoDomainDeleteModalOpen, setIsSsoDomainDeleteModalOpen] =
    useState(false);

  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(domainSchema),
  });

  // Update domains when domainNmaeData changes
  useEffect(() => {
    if (domainNameData?.domain_name?.length > 0) {
      setDomains(domainNameData.domain_name);
    } else {
      setDomains([]);
    }
  }, [domainNameData]);

  const updateFunction = async (updatedDomain: Array<string>) => {
    try {
      const response = await updateCustomerDetails({
        customerId,
        bodyData: {
          domain_name: updatedDomain,
          country: domainNameData.country,
        },
      });
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  const handleAddDomain = handleSubmit(async (data: any) => {
    const isDuplicateDomain = domains.find(
      (domain: any) =>
        domain.toLowerCase() === data?.domain?.trim().toLowerCase(),
    );
    if (isDuplicateDomain) {
      setError("domain", {
        type: "manual",
        message: "This domain is already in use",
      });
    } else {
      try {
        if (data?.domain?.trim() !== "") {
          const updatedDomain = [...domains, data.domain.trim()];
          const resData = await updateFunction(updatedDomain);
          setDomains([...domains, data.domain.trim()]);
          if (resData) {
            reset();
            toast.success("Domain added successfully");
          }
          setShowTextField(false); // Hide the text field after adding a domain
        }
      } catch (error: any) {
        if (typeof error === "string") {
          toast.error(error || "Unable to update domain");
        } else {
          toast.error(error?.message || "Unable to update domain");
        }
      }
    }
  });
  const onNormalDomainDeleteClick = async (domainNameToBeDeleted: string) => {
    setDeleteModallist(
      domains?.filter(
        (domainName: string) => domainName !== domainNameToBeDeleted,
      ),
    );
    setIsDeleteModalOpen(true);
  };
  const onSsoDomainDeleteClick = async (ssoDomainId: string) => {
    setSsoDomainIdToBeDeleted(ssoDomainId);
    setIsSsoDomainDeleteModalOpen(true);
  };

  const handleDeleteClick = async () => {
    try {
      const resData = await updateFunction(deletModalList);
      if (resData) {
        setIsDeleteModalOpen(false);
        toast.success("Domain removed successfully");
        setDeleteModallist([]);
      }
    } catch (error: any) {
      if (typeof error === "string") {
        toast.error(
          error || "Can't delete domain at the moment. Please try again later",
        );
      } else {
        toast.error(
          error?.response?.data?.message ||
            "Can't delete domain at the moment. Please try again later",
        );
      }
    }
  };
  const { mutateAsync: deleteSsoDomain, isLoading: isDeleteSsoDomainLoading } =
    useDeleteSsoConfig();
  const handleSsoDomainDelete = async () => {
    if (ssoDomainIdToBeDeleted) {
      try {
        const resData = await deleteSsoDomain(ssoDomainIdToBeDeleted);
        toast.success("SSO domain deleted successfully");
        setIsSsoDomainDeleteModalOpen(false);
      } catch (error: any) {
        if (typeof error === "string") {
          toast.error(
            error ||
              "Can't delete SSO domain at the moment. Please try again later",
          );
        } else {
          toast.error(
            error?.response?.data?.message ||
              "Can't delete SSO domain at the moment. Please try again later",
          );
        }
      }
    }
  };

  const onSsoDomainClick = (domainId: string) => {
    const ssoDetail = ssoDetails?.find(
      (sso: any) => sso.logto_connector_id === domainId,
    );
    if (ssoDetail) {
      setDrawweOpen({ open: true, mode: "edit", data: ssoDetail });
    }
  };

  const domainList = useMemo(() => {
    const getSsoDomains = () => {
      if (ssoDetails && ssoDetails?.length > 0) {
        // const ssoDomains = ssoDetails?.map((ssoDetail: any) => {
        //   if (ssoDetail?.domains?.length > 0) {
        //     return {
        //       domainName: ssoDetail?.domains?.[0]?.domain,
        //       domainId: ssoDetail?.logto_connector_id,
        //     };
        //   } else {
        //     return null;
        //   }
        // });
        const ssoDomains = ssoDetails?.map((ssoDetail: any) => {
          if (ssoDetail?.domain_name) {
            return {
              domainName: ssoDetail?.domain_name,
              domainId: ssoDetail?.logto_connector_id,
            };
          } else {
            return null;
          }
        });
        return ssoDomains
          ?.filter((ssoDomain: any) => ssoDomain !== null)
          ?.map((ssoDomain: any) => ({
            domainName: ssoDomain?.domainName,
            domainId: ssoDomain?.domainId,
            isSsoDomain: true,
          }));
      } else {
        return [];
      }
    };
    const ssoDomains = getSsoDomains();
    const normalDomains = domains
      ? domains?.map((domain: string) => ({
          domainName: domain,
          isSsoDomain: false,
        }))
      : [];
    const allDomains = [...ssoDomains, ...normalDomains];
    return allDomains?.map(
      ({
        domainName,
        isSsoDomain,
        domainId,
      }: {
        domainName: string;
        isSsoDomain: boolean;
        domainId?: string;
      }) => (
        <Box
          sx={{ borderBottom: "1px solid #E3E3E3", marginBottom: "2px" }}
          key={domainName}
        >
          <Box
            sx={{
              padding: "8px 24px 8px 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "55px",
              cursor: isSsoDomain ? "pointer" : "default",
            }}
            onClick={() => isSsoDomain && onSsoDomainClick(domainId || "")}
          >
            <Stack
              direction="row"
              gap="24px"
              sx={{ width: "100%", justifyContent: "space-between" }}
            >
              <Typography
                variant="body-medium"
                sx={{
                  // width: "260px",
                  maxWidth: "225px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {domainName}
              </Typography>
              {isSsoDomain && (
                <Typography
                  variant="caption-xs"
                  color={theme.palette.primary.main}
                  bgcolor={theme.palette.secondary["purple-tint-3"]}
                  borderRadius={"4px"}
                  p="4px 16px 4px 8px"
                >
                  SSO
                </Typography>
              )}
            </Stack>
            {/* {haveFullAccess && !isSsoDomain && ( */}
            {haveFullAccess && (
              <Box sx={{ marginLeft: "6px" }}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    isSsoDomain
                      ? onSsoDomainDeleteClick(domainId || "")
                      : onNormalDomainDeleteClick(domainName);
                  }}
                  color="inherit"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      ),
    );
  }, [domains, ssoDetails, showTextField]);

  const NoDomainListComponent = () => {
    if (domainList.length === 0 && !showTextField) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexDirection: "column",
            width: "100%",
            mt: "40px",
          }}
        >
          <img
            src={dataDashboardImg}
            style={{ width: "152px", height: "125px" }}
            alt="No Domains Found"
          />
          <Typography variant="body-medium">No Domains Found</Typography>
        </Box>
      );
    }
    return null; // You may also return an empty fragment <></> if you prefer
  };

  const inputField = useMemo(
    () =>
      showTextField && (
        <Box
          sx={{
            borderBottom: "1px solid #E3E3E3",
            padding: "9px 0px 9px 0px",
          }}
        >
          <Input
            control={control}
            name="domain"
            placeholder="Type Domain here..."
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAddDomain();
              }
            }}
            sx={{
              padding: "0px 5px",
              border: "none",
            }}
            noBorder={true}
            hideHelperBox
            autoFocus
            // @ts-ignore
            errorMessage={errors?.domain?.message || ""}
          />
        </Box>
      ),
    [showTextField, newDomain, errors?.domain?.message],
  );

  return (
    <Stack sx={{ width: "387px", minHeight: "236px" }}>
      {/* header */}
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          padding: "18px 24px 18px 24px",
        }}
      >
        <Typography variant="h6">Domains</Typography>
        {haveFullAccess && (
          <Button
            variant="secondary"
            endIcon={<PlusIcon endIconSize={"24px"} />}
            size="small"
            onClick={() => setShowTextField((prev) => !prev)}
            // disabled={isDefaultCustomer}
          >
            Domain
          </Button>
        )}
      </Stack>
      <Divider />
      {/* List of domains */}
      <Box
        sx={{
          maxHeight: "332px",
          minHeight: "282px",
          // bgcolor: "red",
        }}
      >
        <Box
          sx={{
            maxHeight: "282px",
            // bgcolor: "red",
            overflow: "auto",
          }}
        >
          {domainList}
        </Box>
        <>
          {/* Input for adding domain */}
          {inputField && <Box sx={{ height: "55px" }}> {inputField}</Box>}
        </>
        <>
          <NoDomainListComponent />
        </>
      </Box>

      {haveFullAccess && isDeleteModalOpen && (
        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          onPrimaryButtonClick={handleDeleteClick}
          onSecondaryButtonClick={() => setIsDeleteModalOpen(false)}
          title="Delete Domain!"
          tagline="Are you sure you want to delete?"
          isLoading={isUpdateCustomerDetailsLoading}
        />
      )}
      {haveFullAccess && isSsoDomainDeleteModalOpen && (
        <DeleteConfirmationModal
          open={isSsoDomainDeleteModalOpen}
          onPrimaryButtonClick={handleSsoDomainDelete}
          onSecondaryButtonClick={() => setIsSsoDomainDeleteModalOpen(false)}
          title="Delete SSO Domain!"
          tagline="Are you sure you want to delete?"
          isLoading={isDeleteSsoDomainLoading}
        />
      )}
    </Stack>
  );
};

export { DomainComponent };
